import {
  Medication,
  MedicationAdministration,
  MedicationDispense,
  MedicationRequest,
  MedicationStatement,
} from "@medplum/fhirtypes";
import { ISO_DATE } from "@metriport/shared/common/date";
import { ColDef } from "ag-grid-community";
import dayjs from "dayjs";
import { GenerateTableDataParams } from "..";
import { MrFilterSetting } from "../../../../../api/settings";
import { EhrName } from "../../../../../domain/ehr";
import { MappedConsolidatedResources } from "../../../shared-logic/consolidated-context/reducer";
import {
  compare,
  createEhrActionColumnDef,
  filterByDate,
  getFirstCodeSpecified,
  getResourcesFromBundle,
  NDC_CODE,
  RX_NORM_CODE,
  skipColumnsIfNoData,
} from "../shared";

export type MedicationRowData = {
  id: string;
  medication: string;
  dose: string;
  code: string;
  quantity: string;
  lastFillDate: string;
  date: string;
  originalData: MedicationWithRefs;
  ehrAction?: string;
};

export type MedicationWithRefs = {
  medication: Medication;
  administration: MedicationAdministration[];
  dispense: MedicationDispense[];
  statement: MedicationStatement[];
  requests: MedicationRequest[];
};

export const medicationTableData = ({
  bundle,
  tableFilters,
  ehrActions,
}: GenerateTableDataParams) => {
  const columnDefs: ColDef<MedicationRowData>[] = [
    { field: "id", hide: true },
    { field: "medication" },
    { field: "dose" },
    { field: "quantity" },
    { field: "code" },
    { field: "lastFillDate", cellRenderer: (params: { value: string }) => params.value || "-" },
    { field: "date", sort: tableFilters?.stringFilter ? undefined : "desc" },
    { field: "originalData", hide: true },
  ];

  if (
    ehrActions &&
    ehrActions.write &&
    (ehrActions.name === EhrName.Athena ||
      ehrActions.name === EhrName.Canvas ||
      ehrActions.name === EhrName.Healthie)
  ) {
    const columnDef = createEhrActionColumnDef<MedicationWithRefs, MedicationRowData>({
      ehrActions,
      path: "medication",
      filter: medicationWithRefs => isShowWriteBackButton(ehrActions.name, medicationWithRefs),
    });
    columnDefs.push(columnDef);
  }

  const medications = getMedications(bundle);
  const data = getMedicationRowData({ medications, tableFilters });
  // const hasLastFillDateData = data.some(row => row.lastFillDate && row.lastFillDate !== "-");
  const skipColumns = new Set<string>(["lastFillDate", "quantity"]); // always skip these columns, TODO: find a way to better display when many are empty
  // if (!hasLastFillDateData) skipColumns.add("lastFillDate");

  return {
    columnDefs: skipColumnsIfNoData(columnDefs, skipColumns),
    rowData: data,
  };
};

function getMedications(bundle: MappedConsolidatedResources | undefined): MedicationWithRefs[] {
  const medicationsWithRefs: MedicationWithRefs[] = [];

  const medications = getResourcesFromBundle<Medication>(bundle, "Medication");
  const medicationAdministration: MedicationAdministration[] =
    getResourcesFromBundle<MedicationAdministration>(bundle, "MedicationAdministration");
  const medicationDispense: MedicationDispense[] = getResourcesFromBundle<MedicationDispense>(
    bundle,
    "MedicationDispense"
  );
  const medicationStatement: MedicationStatement[] = getResourcesFromBundle<MedicationStatement>(
    bundle,
    "MedicationStatement"
  );
  const medicationRequest: MedicationRequest[] = getResourcesFromBundle<MedicationRequest>(
    bundle,
    "MedicationRequest"
  );

  for (const medication of medications) {
    const medicationWithRefs = getMedicationWithRefs(
      medication,
      medicationAdministration,
      medicationDispense,
      medicationStatement,
      medicationRequest
    );

    medicationsWithRefs.push(medicationWithRefs);
  }

  return medicationsWithRefs;
}

export function getMedicationWithRefs(
  medication: Medication,
  medicationAdministrations: MedicationAdministration[],
  medicationDispenses: MedicationDispense[],
  medicationStatements: MedicationStatement[],
  medicationRequests: MedicationRequest[]
): MedicationWithRefs {
  const medicationWithRef: MedicationWithRefs = {
    medication,
    administration: [],
    dispense: [],
    statement: [],
    requests: [],
  };

  for (const medicationTypes of [
    ...medicationAdministrations,
    ...medicationDispenses,
    ...medicationStatements,
    ...medicationRequests,
  ]) {
    const medRefId = getMedicationReferenceId(medicationTypes) || "";

    if (medRefId === medication.id) {
      if (medicationTypes.resourceType === "MedicationAdministration") {
        medicationWithRef.administration.push(medicationTypes);
      } else if (medicationTypes.resourceType === "MedicationDispense") {
        medicationWithRef.dispense.push(medicationTypes);
      } else if (medicationTypes.resourceType === "MedicationStatement") {
        medicationWithRef.statement.push(medicationTypes);
      } else if (medicationTypes.resourceType === "MedicationRequest") {
        medicationWithRef.requests.push(medicationTypes);
      }
    }
  }

  return medicationWithRef;
}

export function getMedicationReferenceId(
  medication:
    | MedicationAdministration
    | MedicationDispense
    | MedicationStatement
    | MedicationRequest
): string | undefined {
  if (medication.medicationReference?.reference) {
    return medication.medicationReference.reference.split("/")[1];
  }

  return undefined;
}

function getMedicationRowData({
  medications,
  tableFilters,
}: {
  medications: MedicationWithRefs[];
  tableFilters: MrFilterSetting | undefined;
}): MedicationRowData[] {
  return medications
    .map(medicationWithRefs => ({
      id: medicationWithRefs.medication.id ?? "-",
      medication: medicationWithRefs.medication.code?.text ?? "-",
      dose: getMedicationDose(medicationWithRefs),
      quantity: getMedicationQuantity(medicationWithRefs),
      code: getMedicationCode(medicationWithRefs),
      lastFillDate: getLastFillDate(medicationWithRefs),
      date: getStartDate(medicationWithRefs),
      originalData: medicationWithRefs,
    }))
    .filter(row => filterByDate(row.date, tableFilters?.dateFilter))
    .sort((a, b) =>
      compare(
        { ...a, originalData: undefined, id: undefined },
        { ...b, originalData: undefined, id: undefined },
        tableFilters?.stringFilter
      )
    )
    .reduce((acc, curr) => {
      const existing = acc.find(
        row => row.medication === curr.medication && row.dose === curr.dose
      );
      if (existing) {
        if (dayjs(curr.date).isAfter(dayjs(existing.date))) {
          return acc.map(row => (row.medication === curr.medication ? curr : row));
        }
        return acc;
      }
      return [...acc, curr];
    }, [] as MedicationRowData[]);
}

export function getMedicationDose(medicationWithRefs: MedicationWithRefs): string {
  const latestAdministered = getLatestAdministered(medicationWithRefs.administration);

  const adminDosage = latestAdministered?.dosage;
  const hasValidDosage = adminDosage?.dose?.value && adminDosage?.dose?.unit;
  const adminInstructions = hasValidDosage
    ? `${adminDosage?.dose?.value} ${adminDosage?.dose?.unit}`
    : undefined;

  return adminInstructions ?? "-";
}

function getMedicationQuantity(medicationWithRefs: MedicationWithRefs): string {
  const latestDispensed = getLatestDispensed(medicationWithRefs.dispense);
  const latestStatement = getLatestStatement(medicationWithRefs.statement);

  const { value: dValue, unit: dUnit } = latestDispensed?.quantity || {};
  const mainQuantity = dValue && dUnit ? `${dValue} ${dUnit}` : undefined;

  const { value: sValue, unit: sUnit } =
    latestStatement?.dosage?.[0]?.doseAndRate?.[0]?.doseQuantity || {};
  const fallbackQuantity = sValue && sUnit ? `${sValue} ${sUnit}` : undefined;

  return mainQuantity ?? fallbackQuantity ?? "-";
}

function getLastFillDate(medicationWithRefs: MedicationWithRefs): string {
  const latestDispensed = getLatestDispensed(medicationWithRefs.dispense);
  const hasValidDate = latestDispensed?.whenHandedOver;
  return hasValidDate ? dayjs(latestDispensed.whenHandedOver).format(ISO_DATE) : "-";
}

function getMedicationCode(medicationWithRefs: MedicationWithRefs): string {
  const coding = getFirstCodeSpecified(medicationWithRefs.medication?.code?.coding ?? [], [
    RX_NORM_CODE,
    NDC_CODE,
  ]);

  if (coding) {
    return `${coding.system}: ${coding.code}`;
  }

  return "-";
}

function getStartDate(medicationWithRefs: MedicationWithRefs): string {
  const latestAdministered = getLatestAdministered(medicationWithRefs.administration);
  const latestDispensed = getLatestDispensed(medicationWithRefs.dispense);
  const latestStatement = getLatestStatement(medicationWithRefs.statement);
  const latestRequest = getLatestRequest(medicationWithRefs.requests);

  const administeredTime =
    latestAdministered?.effectiveDateTime ||
    latestAdministered?.effectivePeriod?.start ||
    latestAdministered?.effectivePeriod?.end;
  const statementTime =
    latestStatement?.effectiveDateTime ||
    latestStatement?.effectivePeriod?.start ||
    latestStatement?.effectivePeriod?.end;
  const dispensedTime = latestDispensed?.whenHandedOver;
  const requestTime = latestRequest?.authoredOn;

  const time = administeredTime || statementTime || dispensedTime || requestTime;

  if (time) {
    return dayjs(time).format(ISO_DATE);
  }

  return "-";
}

export function getLatestAdministered(
  administrations: MedicationAdministration[]
): MedicationAdministration | undefined {
  return administrations.sort((a, b) => {
    const aTime = a.effectiveDateTime || a.effectivePeriod?.start || a.effectivePeriod?.end;
    const bTime = b.effectiveDateTime || b.effectivePeriod?.start || b.effectivePeriod?.end;

    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}

export function getLatestDispensed(
  dispenses: MedicationDispense[]
): MedicationDispense | undefined {
  return dispenses.sort((a, b) => {
    const aTime = a.whenHandedOver;
    const bTime = b.whenHandedOver;
    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}

export function getLatestStatement(
  statements: MedicationStatement[]
): MedicationStatement | undefined {
  return statements.sort((a, b) => {
    const aTime = a.effectiveDateTime || a.effectivePeriod?.start || a.effectivePeriod?.end;
    const bTime = b.effectiveDateTime || b.effectivePeriod?.start || b.effectivePeriod?.end;
    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}

export function getLatestRequest(requests: MedicationRequest[]): MedicationRequest | undefined {
  return requests.sort((a, b) => {
    const aTime = a.authoredOn;
    const bTime = b.authoredOn;
    return dayjs(bTime).diff(dayjs(aTime));
  })[0];
}

function isShowWriteBackButton(ehr: EhrName, medicationWithRefs: MedicationWithRefs): boolean {
  if (ehr === EhrName.Healthie) return true;
  if (medicationWithRefs.statement.length < 1) return false;
  const startDates = medicationWithRefs.statement.flatMap(statement => {
    const startDate = statement.effectiveDateTime ?? statement.effectivePeriod?.start;
    if (!startDate) return [];
    return [startDate];
  });
  if (startDates.length < 1) return false;
  const medication = medicationWithRefs.medication;
  const rxNormCoding = getFirstCodeSpecified(medication.code?.coding ?? [], [RX_NORM_CODE]);
  if (!rxNormCoding) return false;
  if (ehr === EhrName.Canvas) {
    if (!rxNormCoding.display) return false;
    const statusesAndDosagesExist = medicationWithRefs.statement.find(statement => {
      const status = statement.status;
      if (!status) return false;
      const dosageText = statement.dosage?.[0]?.text;
      if (!dosageText) return false;
      return true;
    });
    if (!statusesAndDosagesExist) return false;
  }
  return true;
}
